import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Passenger } from 'src/app/classes/passenger';
import { NonEmployeePickupWarnStatus } from 'src/app/classes/non-employee-pickup-warn-status';
import { ProfileService } from 'src/app/services/profile.service';
import { Profile } from 'src/app/classes/profile';
import { Roles } from 'src/app/common/roles';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DriverTask } from 'src/app/classes/driver-task';
import { AddOnDetail } from 'src/app/classes/add-on-detail';

@Component({
  standalone: false,
  selector: 'app-outbound-table',
  templateUrl: './outbound-table.component.html',
  styleUrls: [ './outbound-table.component.scss' ]
})
export class OutboundTableComponent implements OnInit, OnChanges {
  @Input() outboundTasks: (DriverTask)[] = [];
  @Input() type: 'SELECTED' | 'DROPOFFS' | 'DEFAULT' = 'DEFAULT';
  @Input() expandedOutbound: DriverTask = undefined;
  @Output() selectOutbound = new EventEmitter<DriverTask>();
  @Output() unselectOutbound = new EventEmitter<DriverTask>();
  @Output() dropOffTask = new EventEmitter<DriverTask>();
  @Output() expandOutbound = new EventEmitter<DriverTask>();

  columns: string[] = [ 'Flight', 'Dest', 'Tail', 'Ramp/Spot', 'Departure' ];
  isCoordinator = false;

  outboundForm: FormGroup = this.fb.group({});

  constructor(readonly profileService: ProfileService, readonly fb: FormBuilder) {}

  ngOnInit(): void {
    // Used to determine if the user can modify the coordinator notes section
    this.profileService
      .getProfileObs()
      .subscribe((profile: Profile) => (this.isCoordinator = profile.userRoles.includes(Roles.COORDINATOR)));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.outboundTasks && changes.outboundTasks.previousValue) {
      this.maintainTasksStates(this.outboundTasks, changes.outboundTasks.previousValue);

      // If this table has an expanded task, ensure that it remains expanded when the list refreshes due to the EventsDeltaService
      if (this.expandedOutbound && !this.outboundTasks.includes(this.expandedOutbound)) {
        this.expandedOutbound = this.outboundTasks.find(
          (outboundTask: DriverTask) => outboundTask.taskName === this.expandedOutbound.taskName
        );
      }
    }
  }

  // When the list of tasks is refreshed, maintain the states of the selected items and notes
  maintainTasksStates(currTasks: (DriverTask)[], prevTasks: (DriverTask)[]): void {
    currTasks.forEach((currTask: DriverTask) => {
      const prevTask: DriverTask = prevTasks.find(
        (previousTask: DriverTask) => currTask.taskName === previousTask.taskName
      );
      if (prevTask) {
        if (currTask.passengers && prevTask.passengers) {
          this.maintainSelectedItemsStates(currTask.passengers, prevTask.passengers);
        }
        if (currTask.addOnDetails && prevTask.addOnDetails) {
          this.maintainSelectedItemsStates(currTask.addOnDetails, prevTask.addOnDetails);
        }
        if (currTask.specialInstructions !== prevTask.specialInstructions) {
          currTask.specialInstructions += prevTask.specialInstructions;
        }
        if (currTask.driverNotes !== prevTask.driverNotes) {
          currTask.driverNotes += prevTask.driverNotes;
        }
      }
    });
  }

  // Update the states of a list of passengers or addon details to the states they were previously
  maintainSelectedItemsStates(currItems: (Passenger | AddOnDetail)[], prevItems: (Passenger | AddOnDetail)[]): void {
    currItems.forEach((passenger: Passenger) => {
      const prevPassenger = prevItems.find((previousPassenger: Passenger) => passenger.id === previousPassenger.id);
      passenger.selected = prevPassenger ? prevPassenger.selected : passenger.selected;
    });
  }

  // Get list of passengers who do not have the jumpseat role
  getCrewmembers(outbound: DriverTask): Passenger[] {
    return outbound.passengers && outbound.passengers.length
      ? outbound.passengers.filter((passenger: Passenger) => passenger.role !== 'JS')
      : [];
  }

  // Get list of passengers
  getJumpseaters(outbound: DriverTask): Passenger[] {
    return outbound.passengers && outbound.passengers.length
      ? outbound.passengers.filter((passenger: Passenger) => passenger.role === 'JS')
      : [];
  }

  // Get the CSS warning color for a jumpseater
  getJumpseaterStatusColor(passenger: Passenger): string {
    switch (passenger.nonEmployeePickupWarningStatus) {
      case NonEmployeePickupWarnStatus.NOT_PICKED_UP:
        return 'red';
      case NonEmployeePickupWarnStatus.IN_TRANSIT:
        return 'orange';
      case NonEmployeePickupWarnStatus.DROPPED_OFF:
        return '#009933';
      case NonEmployeePickupWarnStatus.NA:
      default:
        return '#333333';
    }
  }

  // Emit to the parent outbound component when a task is selected for the assignment/pickup staging list
  onSelect(outbound: DriverTask): void {
    this.selectOutbound.emit(outbound);
    this.expandedOutbound = undefined;
  }

  // Emit to the parent outbound component when a task is unselected from the assignment/pickup staging list
  onUnselect(outbound: DriverTask): void {
    this.unselectOutbound.emit(outbound);
    this.expandedOutbound = undefined;
  }

  // Template helper function to toggle a selected passenger or addon detail
  toggleSelectItem(item: Passenger | AddOnDetail): void {
    item.selected = !item.selected;
  }
}
